import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};
const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  sendEmail: { ...mutationInitialState },
  emailsByThreadId: { ...fetchDataInitialState },
  emailResponseRecommendations: { ...mutationInitialState },
  syncEmails: { ...mutationInitialState },
  syncOutlookEmails: { ...mutationInitialState },
  undoSendEmail: { ...mutationInitialState },
  defaultPrompt: { ...fetchDataInitialState },
  updateConversationMessage: { ...mutationInitialState },
  createCalendarEvent: { ...mutationInitialState },
  mailbox: { ...fetchDataInitialState, hasMore: true, loadingMore: false },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const sendEmail = thunk('email/sendEmail', async (token, payload) => {
  const r = await axios.post('/us/emails/send/async', payload, {
    params: { 'request-id': uuidv4() },
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 200) return r.data;
  throw new Error('Failed to send email');
});

export const fetchEmailsByThreadId = thunk(
  'email/fetchEmailsByThreadId',
  async (token, { dealId, threadId }) => {
    const r = await axios.get(
      `/us/qai/deals/${dealId}/interactions/threads/${threadId}`,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch emails by thread id');
  },
);

export const generateEmailResponseRecommendations = thunk(
  'email/generateEmailResponseRecommendations',
  async (token, interactionId) => {
    const r = await axios.get(
      `/us/qai/interactions/${interactionId}/email/response-recommendations`,
      {
        params: { requestId: uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to generate email response recommendations');
  },
);

export const syncEmailsThunk = thunk(
  'email/syncEmails',
  async (token, payload) => {
    const r = await axios.post('/us/emails/sync', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}`, 'x-api-version': 'v2' },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to sync emails');
  },
);

export const syncOutlookEmailsThunk = thunk(
  'email/syncOutlookEmails',
  async (token, payload) => {
    const r = await axios.post('/us/emails/sync', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to sync outlook emails');
  },
);

export const undoSendEmailThunk = thunk(
  'email/undoSendEmail',
  async (token, sendEmailRequestId) => {
    const r = await axios.post(
      `/us/emails/send-email/${sendEmailRequestId}/undo-send`,
      null,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to undo send email');
  },
);

export const getDefaultPromptThunk = createAsyncThunk(
  'email/getDefaultPrompt',
  async (_, { rejectWithValue }) => {
    try {
      const r = await axios.get('/cs/conversation/prompt', {
        params: { 'request-id': uuidv4() },
      });
      if (r.status === 200) return r.data;
      return rejectWithValue('Failed to fetch default prompt');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

export const updateConversationMessage = thunk(
  'email/updateConversationMessage',
  async (token, { conId, msgId, payload }) => {
    const r = await axios.put(
      `/cs/conversation/${conId}/messages/${msgId}`,
      payload,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to update conversation message');
  },
);

export const createCalendarEventThunk = thunk(
  'email/createCalendarEvent',
  async (token, payload) => {
    const r = await axios.post('/us/qai/calendar/events/send', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to create calendar event');
  },
);

export const getMailboxThunk = thunk(
  'email/getMailbox',
  async (token, params) => {
    const r = await axios.get('/us/qai/mail-box', {
      params: { requestId: uuidv4(), ...params },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch mailbox');
  },
);

const addM = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].processing = true;
      s[k].done = false;
      s[k].failed = false;
    })
    .addCase(t.fulfilled, (s) => {
      s[k].processing = false;
      s[k].done = true;
      s[k].failed = false;
    })
    .addCase(t.rejected, (s, a) => {
      s[k].processing = false;
      s[k].done = false;
      s[k].failed = true;
      s[k].error = a.payload;
    });

const addF = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].fetching = true;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = false;
    })
    .addCase(t.fulfilled, (s, a) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = true;
      s[k].fetchingFailed = false;
      s[k].data = a.payload;
    })
    .addCase(t.rejected, (s) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = true;
    });

const emailSlice = createSlice({
  name: 'email',
  initialState,
  reducers: {
    updateMailboxEmail: (state, action) => {
      const { id, updates } = action.payload;
      if (!state.mailbox.data) return;
      const idx = state.mailbox.data.findIndex((e) => e.id === id);
      if (idx !== -1) Object.assign(state.mailbox.data[idx], updates);
    },
  },
  extraReducers: (builder) => {
    addM(builder, sendEmail, 'sendEmail');
    addF(builder, fetchEmailsByThreadId, 'emailsByThreadId');
    addM(
      builder,
      generateEmailResponseRecommendations,
      'emailResponseRecommendations',
    );
    addM(builder, syncEmailsThunk, 'syncEmails');
    addM(builder, syncOutlookEmailsThunk, 'syncOutlookEmails');
    addM(builder, undoSendEmailThunk, 'undoSendEmail');
    addF(builder, getDefaultPromptThunk, 'defaultPrompt');
    addM(builder, updateConversationMessage, 'updateConversationMessage');
    addM(builder, createCalendarEventThunk, 'createCalendarEvent');
    builder
      .addCase(getMailboxThunk.pending, (s, a) => {
        if (a.meta.arg?.lastSeen) {
          s.mailbox.loadingMore = true;
        } else {
          s.mailbox.fetching = true;
          s.mailbox.fetchingSuccessful = false;
          s.mailbox.fetchingFailed = false;
          s.mailbox.data = null;
          s.mailbox.hasMore = true;
        }
      })
      .addCase(getMailboxThunk.fulfilled, (s, a) => {
        const isLoadMore = !!a.meta.arg?.lastSeen;
        s.mailbox.fetching = false;
        s.mailbox.loadingMore = false;
        s.mailbox.fetchingSuccessful = true;
        s.mailbox.fetchingFailed = false;
        s.mailbox.data = isLoadMore
          ? [...(s.mailbox.data || []), ...a.payload]
          : a.payload;
        s.mailbox.hasMore = a.payload.length >= 15;
      })
      .addCase(getMailboxThunk.rejected, (s) => {
        s.mailbox.fetching = false;
        s.mailbox.loadingMore = false;
        s.mailbox.fetchingSuccessful = false;
        s.mailbox.fetchingFailed = true;
      });
  },
});

export const { updateMailboxEmail } = emailSlice.actions;
export default emailSlice.reducer;
